/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useTenantsList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing tenants list data fetching and state.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { listTenants } from '../api/tenantsApi';
import type { Tenant } from '../types';
import type { DataGridFetchResult } from '../../../hooks/useDataGrid';

export function useTenantsList() {
  const { user } = useAuthStore();
  const { showError } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug || '';

  const fetchTenants = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const result = await listTenants(tenantSlug, user?.id ? Number(user.id) : 0, params);
      return {
        data: Array.isArray(result.data) ? result.data : [],
        pagination: result.pagination ?? {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: result.pagination?.pages ?? 0
        }
      } as DataGridFetchResult;
    } catch (error) {
      showError(t('tenants.failedToLoadTenant'));
      return {
        data: [] as Tenant[],
        pagination: {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0
        }
      } as DataGridFetchResult;
    }
  }, [tenantSlug, user?.id, showError, t]);

  return { fetchTenants };
}

